import { useEffect, useState, useCallback } from 'react';
import debounce from 'debounce';
import { useLazyQuery } from '@apollo/client';
import { SearchLocationQuery } from '@/query';

function mapLocationsToItems(locationAutocompleteResults) {
  return locationAutocompleteResults.map((item) => ({
    value: item.name,
    data: { type: item.type, name: item.name, id: item.id },
  }));
}

export function useSearchLocations({ value }) {
  const [list, setList] = useState([]);
  const [firstRequestReady, setFirstRequestReady] = useState(false);
  const [getLocation, { data, loading, error }] = useLazyQuery(
    SearchLocationQuery,
    {
      onError: (err) => {
        const wasAborted = String(err).toLowerCase().includes('abort');
        if (!wasAborted) {
          setFirstRequestReady(true);
        }
      },
      onCompleted: (data) => {
        setList(mapLocationsToItems(data.locationAutocomplete.collection));
        setFirstRequestReady(true);
      },
    }
  );
  const findLocation = useCallback(debounce(getLocation, 300), []);

  useEffect(() => {
    if (firstRequestReady && !value) {
      setFirstRequestReady(false);
    }
  }, [value]);

  useEffect(() => {
    if (!value) {
      setList([]);
      return;
    }
    const aborter = new AbortController();
    findLocation({
      variables: {
        locationName: value,
      },
      context: {
        fetchOptions: {
          signal: aborter.signal,
        },
      },
    });
    return () => aborter.abort();
  }, [value]);

  const showLoader = (loading || !data) && !firstRequestReady;
  const noResults =
    !list.length && value?.length > 0 && firstRequestReady && !error;
  return {
    data: list,
    loading: showLoader,
    isEmpty: noResults,
  };
}
